interface EmbedOptions {
  showHeader?: boolean
  showElevation?: boolean
  showPhotos?: boolean
  showPOIs?: boolean
  showUserPOIs?: boolean
  showFlyby?: boolean
  showSidebar?: boolean
  mobileMode?: boolean
  theme?: 'light' | 'dark'
  mapStyle?: 'outdoors' | 'satellite' | 'streets'
  height?: string
  width?: string
  // New simple config options
  startElevationMinimized?: boolean
  startSegmentListMinimized?: boolean
  startPhotosOff?: boolean
  startPOIsOff?: boolean
}

/**
 * Generate an iframe embed code for a single route
 */
export function generateRouteEmbedCode(
  routeId: string, 
  options: EmbedOptions = {},
  origin: string = window.location.origin
): string {
  const params = new URLSearchParams()
  
  // Add options as URL parameters (only if different from defaults)
  if (options.showHeader === false) params.set('header', 'false')
  if (options.showElevation === false) params.set('elevation', 'false')
  if (options.showPhotos === false) params.set('photos', 'false')
  if (options.showPOIs === false) params.set('pois', 'false')
  if (options.showUserPOIs === true) params.set('userpois', 'true')
  if (options.showFlyby === false) params.set('flyby', 'false')
  if (options.showSidebar === false) params.set('sidebar', 'false')
  if (options.mobileMode === true) params.set('mobile', 'true')
  if (options.theme === 'light') params.set('theme', 'light')
  if (options.mapStyle && options.mapStyle !== 'satellite') params.set('mapstyle', options.mapStyle)
  if (options.height && options.height !== '100vh') params.set('height', options.height)
  
  // New simple config options
  if (options.startElevationMinimized === true) params.set('elevationMinimized', 'true')
  if (options.startSegmentListMinimized === true) params.set('segmentListMinimized', 'true')
  if (options.startPhotosOff === true) params.set('photosOff', 'true')
  if (options.startPOIsOff === true) params.set('poisOff', 'true')
  
  const queryString = params.toString()
  const embedUrl = `${origin}/embed/route/${routeId}${queryString ? '?' + queryString : ''}`
  
  const width = options.width || '100%'
  const height = options.height === '100vh' ? '600px' : (options.height || '600px')
  
  return `<iframe 
  src="${embedUrl}"
  width="${width}"
  height="${height}"
  frameborder="0"
  style="border: 0; border-radius: 8px;"
  allowfullscreen>
</iframe>`
}

/**
 * Generate a direct URL for a route embed
 */
export function generateRouteEmbedUrl(
  routeId: string,
  options: EmbedOptions = {},
  origin: string = window.location.origin
): string {
  const params = new URLSearchParams()
  
  if (options.showHeader === false) params.set('header', 'false')
  if (options.showElevation === false) params.set('elevation', 'false')
  if (options.showPhotos === false) params.set('photos', 'false')
  if (options.showPOIs === false) params.set('pois', 'false')
  if (options.showUserPOIs === true) params.set('userpois', 'true')
  if (options.showFlyby === false) params.set('flyby', 'false')
  if (options.showSidebar === false) params.set('sidebar', 'false')
  if (options.mobileMode === true) params.set('mobile', 'true')
  if (options.theme === 'light') params.set('theme', 'light')
  if (options.mapStyle && options.mapStyle !== 'satellite') params.set('mapstyle', options.mapStyle)
  if (options.height && options.height !== '100vh') params.set('height', options.height)
  
  // New simple config options
  if (options.startElevationMinimized === true) params.set('elevationMinimized', 'true')
  if (options.startSegmentListMinimized === true) params.set('segmentListMinimized', 'true')
  if (options.startPhotosOff === true) params.set('photosOff', 'true')
  if (options.startPOIsOff === true) params.set('poisOff', 'true')
  
  const queryString = params.toString()
  return `${origin}/embed/route/${routeId}${queryString ? '?' + queryString : ''}`
}

/**
 * Parse embed options from URL search parameters
 */
export function parseEmbedOptions(searchParams: URLSearchParams): EmbedOptions {
  return {
    showHeader: searchParams.get('header') !== 'false',
    showElevation: searchParams.get('elevation') !== 'false',
    showPhotos: searchParams.get('photos') !== 'false',
    showPOIs: searchParams.get('pois') !== 'false',
    showUserPOIs: searchParams.get('userpois') === 'true',
    showFlyby: searchParams.get('flyby') !== 'false',
    showSidebar: searchParams.get('sidebar') !== 'false',
    mobileMode: searchParams.get('mobile') === 'true',
    theme: (searchParams.get('theme') as 'light' | 'dark') || 'dark',
    mapStyle: (searchParams.get('mapstyle') as 'outdoors' | 'satellite' | 'streets') || 'satellite',
    height: searchParams.get('height') || '100vh',
    // New simple config options
    startElevationMinimized: searchParams.get('elevationMinimized') === 'true',
    startSegmentListMinimized: searchParams.get('segmentListMinimized') === 'true',
    startPhotosOff: searchParams.get('photosOff') === 'true',
    startPOIsOff: searchParams.get('poisOff') === 'true'
  }
}

/**
 * Common embed configurations for different use cases
 */
export const EMBED_PRESETS = {
  // Full-featured embed with mobile layout (no sidebar, route list header)
  full: {
    showHeader: false, // Header info moved to route list
    showElevation: true,
    showPhotos: true,
    showPOIs: true,
    showUserPOIs: false,
    showFlyby: true,
    showSidebar: false, // No sidebar, use route list instead
    mobileMode: true,
    theme: 'dark' as const,
    mapStyle: 'satellite' as const
  },
  
  // Minimal embed with just the map and route
  minimal: {
    showHeader: false,
    showElevation: false,
    showPhotos: false,
    showPOIs: false,
    showUserPOIs: false,
    showFlyby: false,
    showSidebar: false,
    theme: 'dark' as const,
    mapStyle: 'satellite' as const
  },
  
  // Map-focused embed with interactive features but no sidebar
  mapFocused: {
    showHeader: true,
    showElevation: true,
    showPhotos: true,
    showPOIs: true,
    showUserPOIs: false,
    showFlyby: true,
    showSidebar: false,
    theme: 'dark' as const,
    mapStyle: 'satellite' as const
  },
  
  // Blog/article embed - good for embedding in content
  article: {
    showHeader: true,
    showElevation: true,
    showPhotos: false,
    showPOIs: false,
    showUserPOIs: false,
    showFlyby: false,
    showSidebar: true,
    theme: 'light' as const,
    mapStyle: 'outdoors' as const,
    height: '500px'
  }
}

/**
 * Generate embed code using a preset configuration
 */
export function generatePresetEmbedCode(
  routeId: string,
  preset: keyof typeof EMBED_PRESETS,
  customOptions: Partial<EmbedOptions> = {},
  origin: string = window.location.origin
): string {
  const presetOptions = EMBED_PRESETS[preset]
  const finalOptions = { ...presetOptions, ...customOptions }
  return generateRouteEmbedCode(routeId, finalOptions, origin)
}